import { useState, useEffect } from "react";
import { workspaceApi, formatApiError } from "../lib/api";
import { Progress } from "../components/ui/progress";
import { PageHeader, StatCard, SectionCard } from "../components/ui/page-shell";
import { Button } from "../components/ui/button";
import { Loader2, TrendingUp, Bot, MessageSquare, Cpu, BadgePercent, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Usage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daily, setDaily] = useState([]);

  useEffect(() => { loadUsage(); }, []);

  const loadUsage = async () => {
    try {
      const res = await workspaceApi.getUsage();
      setUsage(res.data);
      try { const dr = await workspaceApi.usageDaily(30); setDaily(dr?.data?.points || []); } catch (_) {}
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  const chatbotPercent = usage?.chatbots_limit ? (usage.chatbots_used / usage.chatbots_limit) * 100 : 0;
  const limitDisplay = usage?.chatbots_limit === -1 ? "Unlimited" : (usage?.chatbots_limit ?? "—");

  return (
    <div className="space-y-6" data-testid="usage-page">
      <PageHeader title="Usage & Billing" subtitle="Track your monthly consumption and manage your plan." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard data-testid="stat-chatbots" label="Chatbots" value={usage?.chatbots_used ?? 0} icon={Bot} accent="blue" helper={`of ${limitDisplay} active`} />
        <StatCard data-testid="stat-messages" label="Messages This Month" value={(usage?.messages_this_month ?? 0).toLocaleString()} icon={MessageSquare} accent="violet" helper="resets on billing day" />
        <StatCard data-testid="stat-tokens" label="Tokens Used" value={(usage?.tokens_used ?? 0).toLocaleString()} icon={Cpu} accent="emerald" helper="all time" />
      </div>

      <SectionCard title="Plan capacity" description="Your current chatbot usage versus plan limit.">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-900">Chatbots in workspace</p>
              <p className="text-xs text-slate-500">{usage?.chatbots_used ?? 0} / {limitDisplay}</p>
            </div>
            <Progress value={Math.min(100, chatbotPercent)} className="h-2" />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Have a coupon?" description="Apply a promo code to unlock discounts or extended credits." >
        <div className="flex gap-2 max-w-md">
          <input type="text" placeholder="Enter coupon code" data-testid="coupon-input" className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-blue-600" />
          <Button data-testid="apply-coupon-btn" className="bg-blue-600 hover:bg-blue-700"><BadgePercent className="w-4 h-4 mr-1.5" /> Apply</Button>
        </div>
      </SectionCard>

      {daily.length > 0 && (
        <SectionCard title="Daily Token Usage" description="Tokens consumed per day over the last 30 days." padding="p-4" data-testid="usage-daily-card">
          <div className="flex items-end gap-1 h-24">
            {daily.map((p, i) => {
              const max = Math.max(...daily.map(x => x.tokens || 0), 1);
              const h = Math.max(2, Math.round(((p.tokens || 0) / max) * 80));
              return (
                <div key={i} className="flex-1 bg-blue-500/60 hover:bg-blue-500 transition-colors" style={{ height: h + "px" }} title={`${p.date}: ${(p.tokens || 0).toLocaleString()} tokens (${p.messages} msgs)`} />
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>{daily[0]?.date}</span>
            <span>{daily[daily.length-1]?.date}</span>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
